import { GAME_OVERRIDE, MATCH_VARIANT, TOURNAMENT_GAME_RESULT, } from "../../constants";
import { TournamentError } from "../../errors";
import { getBufferDuration, hasCommencedByMoveCount, } from "../../utils/match-helper";
import { assignGamePlayers, generateGameId, getTournamentResultsFromMatch, hasResultMismatch, } from "../utils/gameUtils";
export class TournamentGameService {
    repository;
    constructor(repository) {
        this.repository = repository;
    }
    async getGame(tournamentId, gameId) {
        return this.repository.getGame(tournamentId, gameId);
    }
    async getRoundGames(tournamentId, roundNumber) {
        const games = await this.repository.getGames(tournamentId);
        return games.filter((g) => g.roundNumber === roundNumber);
    }
    assignGamePlayers(game, whiteUsername, blackUsername) {
        assignGamePlayers(game, whiteUsername, blackUsername);
    }
    async saveRoundGame(tournamentId, game) {
        const currentGame = await this.getGame(tournamentId, game.gameId);
        if (!currentGame) {
            await this.repository.createGame(tournamentId, game);
            console.log("added round game", game.gameId);
        }
        else if (this.shouldUpdateGame(currentGame, game)) {
            await this.repository.updateGame(tournamentId, game.gameId, game);
            console.log("updated round game", game.gameId);
        }
    }
    async saveRoundGames(tournamentId, games) {
        if (!games.length)
            return;
        // Get all existing games for this tournament to check which ones need to be created vs updated
        const existingGames = await this.repository.getGames(tournamentId);
        const gamesToCreate = [];
        const gamesToUpdate = [];
        // Separate games into create vs update operations
        for (const game of games) {
            const existingGame = existingGames.find((g) => g.gameId === game.gameId);
            if (!existingGame) {
                gamesToCreate.push(game);
            }
            else if (this.shouldUpdateGame(existingGame, game)) {
                gamesToUpdate.push(game);
            }
        }
        if (gamesToCreate.length > 0) {
            console.log(`Creating ${gamesToCreate.length} round games in batch`);
            await this.repository.createGames(tournamentId, gamesToCreate);
        }
        if (gamesToUpdate.length > 0) {
            console.log(`Updating ${gamesToUpdate.length} round games`);
            await this.repository.updateGames(tournamentId, gamesToUpdate);
        }
    }
    shouldUpdateGame(currentGame, updatedGame) {
        return (updatedGame.whiteUsername !== currentGame.whiteUsername ||
            updatedGame.blackUsername !== currentGame.blackUsername ||
            updatedGame.whiteResult !== currentGame.whiteResult ||
            updatedGame.blackResult !== currentGame.blackResult ||
            updatedGame.whiteScoreBeforeGame !== currentGame.whiteScoreBeforeGame ||
            updatedGame.blackScoreBeforeGame !== currentGame.blackScoreBeforeGame ||
            (!currentGame.matchRef && updatedGame.matchRef) ||
            (currentGame.matchRef &&
                currentGame.matchRef.id !== updatedGame.matchGUID));
    }
    async startRoundGame(tournamentId, game, whitePlayer, blackPlayer) {
        // true = needs match creation, false = doesn't need
        if (game.matchRef)
            return false;
        if (!game.whiteUsername || !game.blackUsername)
            return false;
        // Validate players first
        if (!whitePlayer?.user || !blackPlayer?.user) {
            throw new TournamentError(tournamentId, game.roundNumber, "startRoundGame", `Invalid player data for ${!whitePlayer?.user ? game.whiteUsername : game.blackUsername}`);
        }
        // Handle AI vs AI games
        if (whitePlayer.user.aiLevel && blackPlayer.user.aiLevel) {
            const aiResult = this.generateAIGameResult();
            await this.repository.finishGame(tournamentId, game.gameId, {
                ...aiResult,
            });
            return false; // AI vs AI game doesn't need match creation
        }
        // Regular game
        const tournament = await this.repository.getTournament(tournamentId);
        // For offline tournaments, matchGUID is null and we don't create actual matches
        if (game.matchGUID) {
            await this.repository.createMatch({
                match_id: game.matchGUID,
                player1: whitePlayer.user,
                player2: blackPlayer.user,
                gameOverride: GAME_OVERRIDE.TOURNAMENT,
                duration: tournament.baseTimeControl * 60,
                increment: tournament.increment ?? 0,
                buffer_duration: getBufferDuration(tournament.baseTimeControl),
                variant: MATCH_VARIANT.STANDARD,
                initial_fen: tournament.initialFen ?? "",
                tournamentId,
                roundNumber: game.roundNumber,
                board: game.board,
            });
        }
        await this.repository.startGame(tournamentId, game);
        return true; // Regular game needs match creation
    }
    generateAIGameResult() {
        const r = Math.random();
        if (r < 0.4) {
            return {
                whiteResult: TOURNAMENT_GAME_RESULT.WIN,
                blackResult: TOURNAMENT_GAME_RESULT.LOSS,
            };
        }
        if (r < 0.8) {
            return {
                whiteResult: TOURNAMENT_GAME_RESULT.LOSS,
                blackResult: TOURNAMENT_GAME_RESULT.WIN,
            };
        }
        return {
            whiteResult: TOURNAMENT_GAME_RESULT.DRAW,
            blackResult: TOURNAMENT_GAME_RESULT.DRAW,
        };
    }
    isByeGame(game) {
        return !game.whiteUsername || !game.blackUsername;
    }
    validateGame(game) {
        return !!(game &&
            game.roundNumber > 0 &&
            game.board > 0 &&
            game.gameId &&
            (game.whiteUsername || game.blackUsername));
    }
    hasResultMismatch(game, match, moves) {
        return hasResultMismatch(game, match, moves);
    }
    getTournamentResultsFromMatch(match, moves) {
        return getTournamentResultsFromMatch(match, moves);
    }
    async finishTournamentGame(match, moves) {
        if (!match?.result || !match.tournamentId)
            return;
        const { whiteResult, blackResult } = this.getTournamentResultsFromMatch(match, moves);
        const gameId = generateGameId(match.roundNumber, match.board);
        const tournamentGame = await this.repository.getGame(match.tournamentId, gameId);
        if (!tournamentGame)
            return;
        if (tournamentGame.whiteResult === whiteResult &&
            tournamentGame.blackResult === blackResult &&
            tournamentGame.startTime &&
            tournamentGame.finishTime) {
            return; // match has already been finalized
        }
        await this.repository.finishGame(match.tournamentId, gameId, {
            startTime: match.startedAt,
            finishTime: match.endedAt,
            whiteResult,
            blackResult,
        });
    }
    async forfeitIfNotCommenced(game) {
        if (!game.matchRef || game.finishTime || !game.matchGUID)
            return false;
        const match = await this.repository.getMatch(game.matchGUID);
        if (!match)
            return false;
        const moves = await this.repository.getMatchMoves(game.matchGUID);
        if (hasCommencedByMoveCount(match, moves))
            return false;
        if (!this.hasResultMismatch(game, match, moves) && game.finishTime) {
            return false;
        }
        const { whiteResult, blackResult } = this.getTournamentResultsFromMatch(match, moves);
        const gameUpdate = {
            whiteResult,
            blackResult,
            startTime: match?.startedAt || null,
        };
        // Update both game object and repository
        Object.assign(game, gameUpdate);
        await this.repository.finishGame(match.tournamentId, game.gameId, gameUpdate);
        return true;
    }
    async fixResultIfMismatched(tournamentId, game) {
        if (!game.matchRef || !game.matchGUID)
            return false;
        const match = await this.repository.getMatch(game.matchGUID);
        if (!match)
            return false;
        const moves = await this.repository.getMatchMoves(game.matchGUID);
        if (hasCommencedByMoveCount(match, moves) && !match.result) {
            // TODO: notify admin - match goes into overtime
            return false;
        }
        // Check if results match and game is already finished
        if (!this.hasResultMismatch(game, match, moves)) {
            return false;
        }
        const { whiteResult, blackResult } = this.getTournamentResultsFromMatch(match, moves);
        console.log(`fix game ${game.gameId} result ${whiteResult}-${blackResult}`);
        const gameUpdate = {
            whiteResult,
            blackResult,
            startTime: match.startedAt || null,
            finishTime: match.endedAt || null,
        };
        // Update both game object and repository
        Object.assign(game, gameUpdate);
        await this.repository.finishGame(tournamentId, game.gameId, gameUpdate);
        return true;
    }
}
